import React, { useState, useEffect, useContext } from "react";
import LeaderBoard from "components/leaderboard/leaderboard";
import NextGame from "components/next-game";
import PreviousGames from "components/previous-games";
import { getCurrentWeek, getCurrentYear } from "utils/date-utils";
import { MembershipContext } from "hoc/membership-data";
import instance from "services/api";
import FirstGame from "./dashboard/first-game";
import { permissionsService } from "services/permissions.service";
import GameScore from "./game-score";
import { ToastService, ToastComponent } from "services/toast.service";
import WaitingNextGame from "./empty-state/games/waiting-next-game";
import Spinner from "./functional/spinner";
import { useTranslation } from "react-i18next";
import styles from "./my-dashboard.module.css";

const MyDashboard = () => {
  const [nextGame, setNextGame] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLastGameScoring, setShowLastGameScoring] = useState(false);
  const [pastGames, setPastGames] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const membershipData = useContext(MembershipContext);
  const { t } = useTranslation();

  const { teams } = membershipData;

  if (teams.length > 1) {
    // Display another component if multiple teams exist
  }

  const { team_id, is_in_championship, championship_id } = teams[0];

  useEffect(() => {
    const fetchGames = async () => {
      const year = getCurrentYear();
      const week = getCurrentWeek();

      const endpoints = [
        {
          url: `/games/${team_id}/next-game`,
          setter: setNextGame,
        },
        {
          url: `/games/${team_id}/past-games`,
          setter: setPastGames,
        },
        {
          url: `/leaderboard/${championship_id}/${year}/${week}`,
          setter: setLeaderboard,
        },
      ];

      try {
        const results = await Promise.allSettled(
          endpoints.map((endpoint) => instance.get(endpoint.url))
        );

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            endpoints[index].setter(result.value.data);
          } else {
            console.log(
              `Failed to fetch data from ${endpoints[index].url}:`,
              result.reason
            );
          }
        });
      } catch (error) {
        console.log("An unexpected error occurred:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (is_in_championship) fetchGames();
  }, [team_id]);

  const fetchLastGameScore = async () => {
    if (pastGames.length > 0) {
      const lastGameId = pastGames[0].game_id; // Assuming the last game is the first in the array
      try {
        await instance.get(
          `/game-score-submissions/${lastGameId}/teams/${team_id}`
        );
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // No score submission found for this game so we need to show the scoring component
          setShowLastGameScoring(true);
        } else {
          console.log(`Failed to fetch score for game ${lastGameId}:`, error);
        }
      }
    }
  };

  useEffect(() => {
    const canSubmitGameScore = permissionsService.hasPermission(
      "submit_game_score",
      team_id
    );
    if (canSubmitGameScore) fetchLastGameScore();
  }, [pastGames, team_id]);

  const submitScore = async (submittedScore, opponentScore) => {
    try {
      await instance.post("/game-score-submissions", {
        game_id: pastGames[0].game_id,
        team_id,
        submitted_score: submittedScore,
        opponent_score: opponentScore,
      });

      setShowLastGameScoring(false);
      ToastService.showSuccessToast(t("dashboard.game-score.submitted"));
    } catch (error) {
      console.error("Failed to submit game score:", error);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className={styles.wrapper}>
      <ToastComponent />
      <div className={styles.content}>
        <section className={styles.submission}>
          {showLastGameScoring ? (
            <GameScore
              lastGame={pastGames[0]}
              currentTeam={team_id}
              submitScore={submitScore}
            />
          ) : null}
        </section>
        <div className="dashboard-content">
          <section className={styles["dashboard-left"]}>
            {nextGame ? <NextGame game={nextGame} /> : <WaitingNextGame />}
          </section>
          <section className="dashboard-right">
            {leaderboard.length > 0 ? (
              <>
                <div className="leaderboard">
                  <LeaderBoard leaderboard={leaderboard} team_id={team_id} />
                </div>
                <div className="previous-games">
                  <PreviousGames games={pastGames} team_id={team_id} />
                </div>
              </>
            ) : (
              <FirstGame />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MyDashboard;
